/*
 * The native quick-add "Choose" flow fetches the product page and expects to
 * find the theme's native product-form markup there to fill the modal. Our
 * product template is fully custom, so that markup doesn't exist and the
 * modal opens empty and stuck. Replace it with a lightweight popover that
 * fetches the product's variants directly and adds to cart via the storefront
 * JSON endpoints, independent of the product page's markup.
 */
(() => {
  let activePopover = null;
  let activeButton = null;
  const popoverCleanups = new WeakMap();

  const closePopover = () => {
    if (!activePopover) return;
    popoverCleanups.get(activePopover)?.();
    popoverCleanups.delete(activePopover);
    activePopover.remove();
    activePopover = null;
    activeButton = null;
  };

  const fetchProduct = async (productUrl) => {
    const response = await fetch(`${productUrl}.js`);
    if (!response.ok) throw new Error('Failed to fetch product');
    return response.json();
  };

  const addToCart = async (variantId) => {
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: variantId, quantity: 1, sections: 'cart-drawer-section' }),
    });
    if (!response.ok) throw new Error('Failed to add to cart');
    return response.json();
  };

  /*
   * The native "Add" flow dispatches Shopify's own cart-lines-update event,
   * which `cart-items-component` uses to morph in fresh section HTML. Our
   * popover bypasses that whole system, so the cart drawer never reflected
   * what we'd just added — do the same section swap ourselves.
   */
  const CART_DRAWER_SECTION_ID = 'cart-drawer-section';

  const refreshCartDrawerSection = (sectionHtml) => {
    if (!sectionHtml) return;
    const current = document.getElementById(`shopify-section-${CART_DRAWER_SECTION_ID}`);
    if (!current) return;

    const next = new DOMParser()
      .parseFromString(sectionHtml, 'text/html')
      .getElementById(`shopify-section-${CART_DRAWER_SECTION_ID}`);
    if (next) current.replaceWith(next);
  };

  /*
   * The header count is rendered once, server-side, so it never updates
   * after an AJAX add. Refresh it from the cart's real state whenever
   * anything changes it: the native cart events (product page, cart page,
   * the working single-variant quick-add) and our own popover below.
   */
  const refreshHeaderCartCount = async () => {
    let itemCount;
    try {
      const cart = await (await fetch('/cart.js', { cache: 'no-store' })).json();
      itemCount = cart.item_count;
    } catch {
      return;
    }

    document.querySelectorAll('.fallconi-header__cart-btn').forEach((cartButton) => {
      let countEl = cartButton.querySelector('.fallconi-header__cart-count');

      if (itemCount > 0) {
        if (!countEl) {
          countEl = document.createElement('span');
          countEl.className = 'fallconi-header__cart-count';
          cartButton.appendChild(countEl);
        }
        countEl.textContent = itemCount;
      } else {
        countEl?.remove();
      }
    });
  };

  /*
   * Shopify dispatches this event optimistically, before the add/update
   * request settles — `event.promise` is what resolves once it actually has.
   * Refreshing on the event itself races the request and reads stale cart
   * state, which is why the count looked wrong for single-variant products.
   */
  document.addEventListener('shopify:cart:lines-update', (event) => {
    (event.promise ?? Promise.resolve()).then(refreshHeaderCartCount).catch(() => {});
  });

  const buildPopover = () => {
    const popover = document.createElement('div');
    popover.className = 'fallconi-quick-add-popover';
    popover.innerHTML = `
      <p class="fallconi-quick-add-popover__status">Loading sizes…</p>
      <div class="fallconi-quick-add-popover__row" hidden>
        <div class="fallconi-quick-add-popover__variants"></div>
        <button type="button" class="fallconi-quick-add-popover__add" disabled>Add to cart</button>
      </div>
    `;
    popover.addEventListener('click', (event) => event.stopPropagation());
    return popover;
  };

  const positionPopover = (popover, anchorButton) => {
    const rect = anchorButton.getBoundingClientRect();
    const popoverRect = popover.getBoundingClientRect();
    const gap = 8;

    let left = rect.left - popoverRect.width - gap;
    if (left < 8) left = Math.min(rect.right + gap, window.innerWidth - popoverRect.width - 8);

    let top = rect.top + rect.height / 2 - popoverRect.height / 2;
    top = Math.max(8, Math.min(top, window.innerHeight - popoverRect.height - 8));

    popover.style.left = `${left}px`;
    popover.style.top = `${top}px`;
  };

  const openPopover = (quickAdd, productUrl, anchorButton) => {
    const popover = buildPopover();
    document.body.appendChild(popover);
    positionPopover(popover, anchorButton);
    activePopover = popover;
    activeButton = anchorButton;

    // The button only shows on hover by default; keep it visible while our
    // popover (which isn't a descendant of it, so :hover won't cover it) is open.
    quickAdd.classList.add('fallconi-quick-add-open');

    const reposition = () => positionPopover(popover, anchorButton);
    window.addEventListener('scroll', reposition, { passive: true, capture: true });
    window.addEventListener('resize', reposition);
    const cleanup = () => {
      window.removeEventListener('scroll', reposition, true);
      window.removeEventListener('resize', reposition);
      quickAdd.classList.remove('fallconi-quick-add-open');
    };
    popoverCleanups.set(popover, cleanup);

    const statusEl = popover.querySelector('.fallconi-quick-add-popover__status');
    const rowEl = popover.querySelector('.fallconi-quick-add-popover__row');
    const variantsEl = popover.querySelector('.fallconi-quick-add-popover__variants');
    const addButton = popover.querySelector('.fallconi-quick-add-popover__add');
    let selectedVariantId = null;

    fetchProduct(productUrl)
      .then((product) => {
        statusEl.hidden = true;
        rowEl.hidden = false;

        product.variants.forEach((variant) => {
          const variantButton = document.createElement('button');
          variantButton.type = 'button';
          variantButton.className = 'fallconi-quick-add-popover__variant';
          variantButton.textContent = variant.title;
          variantButton.disabled = !variant.available;

          if (variant.available && !selectedVariantId) {
            selectedVariantId = variant.id;
            variantButton.classList.add('is-active');
          }

          variantButton.addEventListener('click', () => {
            variantsEl
              .querySelectorAll('.fallconi-quick-add-popover__variant')
              .forEach((btn) => btn.classList.remove('is-active'));
            variantButton.classList.add('is-active');
            selectedVariantId = variant.id;
            addButton.disabled = false;
          });

          variantsEl.appendChild(variantButton);
        });

        addButton.disabled = !selectedVariantId;
        reposition();
      })
      .catch(() => {
        statusEl.textContent = 'Could not load sizes.';
      });

    addButton.addEventListener('click', () => {
      if (!selectedVariantId) return;
      addButton.disabled = true;
      addButton.textContent = 'Adding…';

      addToCart(selectedVariantId)
        .then((result) => {
          addButton.textContent = 'Added';
          refreshCartDrawerSection(result.sections?.[CART_DRAWER_SECTION_ID]);
          refreshHeaderCartCount();
          setTimeout(closePopover, 900);
        })
        .catch(() => {
          addButton.textContent = 'Try again';
          addButton.disabled = false;
        });
    });
  };

  document.addEventListener('click', (event) => {
    if (
      activePopover &&
      !event.target.closest('.fallconi-quick-add-popover') &&
      !event.target.closest('.quick-add__button, [data-related-cart-btn]')
    ) {
      closePopover();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') closePopover();
  });

  /*
   * The native button already has Shopify's own click handler bound (it opens
   * the broken modal). Shopify's framework delegates via the `on:click`
   * attribute (a single document-level listener resolves it with
   * `closest('[on\\:click]')`), so cloning alone isn't enough — the attribute
   * itself has to go too.
   */
  const initQuickAdd = (quickAdd) => {
    if (quickAdd.dataset.fallconiQuickAddReady === 'true') return;
    if (quickAdd.dataset.quickAddButton !== 'choose') return;

    const original = quickAdd.querySelector('.quick-add__button');
    if (!original) return;

    quickAdd.dataset.fallconiQuickAddReady = 'true';
    const button = original.cloneNode(true);
    original.replaceWith(button);
    button.removeAttribute('on:click');

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const alreadyOpenForThis = activeButton === button;
      closePopover();
      if (alreadyOpenForThis) return;

      const productUrl = quickAdd.closest('product-card')?.querySelector('.product-card__link')?.href;
      if (!productUrl) return;

      openPopover(quickAdd, productUrl, button);
    });
  };

  /*
   * Same popover, for the plain cart button on the product page's
   * "Similar products" cards — no native quick-add component involved here,
   * so there's no broken modal or `on:click` attribute to strip.
   */
  const initRelatedCartButton = (button) => {
    if (button.dataset.fallconiQuickAddReady === 'true') return;
    button.dataset.fallconiQuickAddReady = 'true';

    button.addEventListener('click', (event) => {
      event.preventDefault();
      event.stopPropagation();

      const alreadyOpenForThis = activeButton === button;
      closePopover();
      if (alreadyOpenForThis) return;

      const productUrl = button.dataset.productUrl;
      if (!productUrl) return;

      openPopover(button, productUrl, button);
    });
  };

  const initAll = (scope) => {
    (scope || document).querySelectorAll('quick-add-component').forEach(initQuickAdd);
    (scope || document).querySelectorAll('[data-related-cart-btn]').forEach(initRelatedCartButton);
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll());
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => initAll(event.target));

  /*
   * The drawer's own outside-click-to-close only fires for clicks on its
   * backdrop (clicks with event.target === the dialog itself), because the
   * listener lives on the dialog and never sees clicks elsewhere in the
   * page. Now that the drawer sits below the header instead of covering it,
   * clicks on that now-visible header strip are exactly that case.
   */
  document.addEventListener('click', (event) => {
    const drawer = document.getElementById('cart-drawer');
    if (!drawer?.hasAttribute('open')) return;
    if (drawer.contains(event.target)) return;
    if (event.target.closest('.fallconi-header__cart-btn')) return;

    drawer.close?.();
  });
})();
